import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { Modal, Field, TextInput, Select, FormActions, dbErrorMessage } from '../components/form';

type Tpl = {
  id: string; name: string; asset_category_id: string | null; service_line_id: string | null;
  version: number; status: string;
};
type Item = {
  id: string; position: number; label: string; label_i18n: Record<string, string> | null; item_type: string; unit: string | null;
  min_value: number | null; max_value: number | null; photo_required: boolean; is_required: boolean;
  zone: string | null; anomaly_priority: string | null;
};

const STATUS_TONE: Record<string, 'open' | 'inprogress' | 'done' | 'onhold'> = {
  draft: 'onhold', published: 'done', archived: 'open',
};
const ITEM_TYPES = ['step', 'reading', 'photo'] as const;
const STATUSES = ['draft', 'published', 'archived'] as const;
const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
const PRIO_TONE: Record<string, string> = { low: 'text-muted', medium: 'text-st-onhold-fg', high: 'text-st-overdue-fg', urgent: 'text-st-overdue-fg font-semibold' };

export function ChecklistTemplates() {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [templates, setTemplates] = useState<Tpl[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [cats, setCats] = useState<{ id: string; name: string }[]>([]);
  const [lines, setLines] = useState<{ id: string; name: string }[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tpl | null>(null);
  const [ni, setNi] = useState({ label: '', item_type: 'step', unit: '', min: '', max: '', photo_required: false, is_required: true, zone: '', anomaly_priority: '' });
  const [itemErr, setItemErr] = useState('');
  const [editItem, setEditItem] = useState<Item | null>(null);

  const loadTemplates = useCallback(async () => {
    const [tpl, it] = await Promise.all([
      supabase.from('checklist_templates').select('id,name,asset_category_id,service_line_id,version,status').is('deleted_at', null).order('name'),
      supabase.from('checklist_template_items').select('template_id').is('deleted_at', null),
    ]);
    setTemplates((tpl.data as Tpl[]) ?? []);
    const c: Record<string, number> = {};
    ((it.data as { template_id: string }[]) ?? []).forEach((x) => { c[x.template_id] = (c[x.template_id] ?? 0) + 1; });
    setCounts(c);
  }, []);
  const loadItems = useCallback(async (tid: string) => {
    const r = await supabase.from('checklist_template_items').select('id,position,label,label_i18n,item_type,unit,min_value,max_value,photo_required,is_required,zone,anomaly_priority').eq('template_id', tid).is('deleted_at', null).order('position');
    setItems((r.data as Item[]) ?? []);
  }, []);

  useEffect(() => {
    loadTemplates();
    supabase.from('asset_categories').select('id,name').is('deleted_at', null).order('name').then((r) => setCats(r.data ?? []));
    supabase.from('service_lines').select('id,code,name_i18n').is('deleted_at', null).order('code').then((r) =>
      setLines(((r.data as any[]) ?? []).map((x) => ({ id: x.id, name: x.name_i18n?.en ?? x.code }))));
  }, [loadTemplates]);
  useEffect(() => { if (selected) loadItems(selected); else setItems([]); }, [selected, loadItems]);

  const catName = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c.name])), [cats]);
  const lineName = useMemo(() => Object.fromEntries(lines.map((l) => [l.id, l.name])), [lines]);
  const scopeLabel = (tp: Tpl) => tp.asset_category_id ? catName[tp.asset_category_id] : tp.service_line_id ? lineName[tp.service_line_id] : '—';
  const current = templates.find((tp) => tp.id === selected) ?? null;

  async function addItem() {
    if (!selected || !ni.label.trim()) { setItemErr(t('form.errRequired')); return; }
    setItemErr('');
    const payload = {
      organization_id: orgId, template_id: selected, position: items.length,
      label: ni.label.trim(), item_type: ni.item_type,
      unit: ni.item_type === 'reading' ? (ni.unit.trim() || null) : null,
      min_value: ni.item_type === 'reading' && ni.min.trim() ? Number(ni.min) : null,
      max_value: ni.item_type === 'reading' && ni.max.trim() ? Number(ni.max) : null,
      photo_required: ni.photo_required, is_required: ni.is_required,
      zone: ni.zone.trim() || null, anomaly_priority: ni.anomaly_priority || null,
    };
    const res = await supabase.from('checklist_template_items').insert(payload as any);
    if (res.error) { setItemErr(dbErrorMessage(t, res.error)); return; }
    setNi({ label: '', item_type: 'step', unit: '', min: '', max: '', photo_required: false, is_required: true, zone: '', anomaly_priority: '' });
    loadItems(selected); loadTemplates();
  }
  async function delItem(id: string) {
    await supabase.from('checklist_template_items').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (selected) { loadItems(selected); loadTemplates(); }
  }
  async function move(idx: number, dir: -1 | 1) {
    const a = items[idx], b = items[idx + dir];
    if (!a || !b) return;
    await Promise.all([
      supabase.from('checklist_template_items').update({ position: b.position }).eq('id', a.id),
      supabase.from('checklist_template_items').update({ position: a.position }).eq('id', b.id),
    ]);
    if (selected) loadItems(selected);
  }

  const sel = 'h-9 rounded-md border border-border bg-card px-2 text-sm outline-none';
  return (
    <Layout header={<Topbar actions={<Button onClick={() => { setEditing(null); setFormOpen(true); }}>+ {t('checklist.new')}</Button>} />}>
      <div className="px-7 pt-6"><h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('checklist.title')}</h1></div>
      <div className="flex flex-wrap items-start gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="min-w-0 max-w-[420px] flex-1 overflow-hidden p-0" style={{ flexBasis: 360 }}>
          {templates.length === 0 ? (
            <div className="p-6 text-sm text-muted">{t('checklist.noTemplates')}</div>
          ) : templates.map((tp) => (
            <button key={tp.id} onClick={() => setSelected(tp.id)}
              className={cn('flex w-full flex-col gap-1.5 border-b border-divider p-4 text-left last:border-0', tp.id === selected ? 'bg-[#F5F7FF] shadow-[inset_3px_0_0_var(--pv-accent)]' : 'hover:bg-bg')}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{tp.name}</span>
                <StatusPill tone={STATUS_TONE[tp.status] ?? 'open'}>{t('checklistStatus.' + tp.status)}</StatusPill>
              </div>
              <span className="text-xs text-muted">{scopeLabel(tp)} · v{tp.version} · {counts[tp.id] ?? 0} {t('checklist.items').toLowerCase()}</span>
            </button>
          ))}
        </Card>

        <Card className="min-w-0 flex-[2] p-0" style={{ flexBasis: 480 }}>
          {!current ? (
            <div className="p-6 text-sm text-muted">{t('checklist.selectTemplate')}</div>
          ) : (
            <div className="flex flex-col">
              <div className="flex items-center justify-between border-b border-divider p-4">
                <div className="flex flex-col gap-0.5">
                  <span className="font-semibold">{current.name}</span>
                  <span className="text-xs text-muted">{scopeLabel(current)} · v{current.version}</span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => { setEditing(current); setFormOpen(true); }}>{t('form.edit')}</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead><tr className="text-left text-xs text-muted">
                    <th className="px-4 py-2.5 font-medium">{t('checklist.itemLabel')}</th>
                    <th className="px-2 py-2.5 font-medium">{t('checklist.itemType')}</th>
                    <th className="px-2 py-2.5 font-medium">{t('checklist.unit')}</th>
                    <th className="px-2 py-2.5 font-medium">{t('checklist.min')}/{t('checklist.max')}</th>
                    <th className="px-2 py-2.5 font-medium">{t('checklist.zone')}</th>
                    <th className="px-2 py-2.5 font-medium">{t('checklist.priority')}</th>
                    <th className="px-2 py-2.5 font-medium">{t('checklist.photoRequired')}</th>
                    <th className="px-4 py-2.5" />
                  </tr></thead>
                  <tbody>
                    {items.length === 0 && <tr><td colSpan={8} className="px-4 py-5 text-muted">{t('checklist.noItems')}</td></tr>}
                    {items.map((it, idx) => (
                      <tr key={it.id} className="border-t border-divider">
                        <td className="px-4 py-2.5 font-medium">{it.label}{it.is_required && <span className="text-st-overdue-fg"> *</span>}</td>
                        <td className="px-2 py-2.5 text-muted">{t('checklistType.' + it.item_type)}</td>
                        <td className="px-2 py-2.5 font-mono text-muted">{it.unit ?? '—'}</td>
                        <td className="px-2 py-2.5 font-mono text-muted">{it.item_type === 'reading' ? `${it.min_value ?? '—'} / ${it.max_value ?? '—'}` : '—'}</td>
                        <td className="px-2 py-2.5 text-muted">{it.zone ?? '—'}</td>
                        <td className={cn('px-2 py-2.5', it.anomaly_priority ? PRIO_TONE[it.anomaly_priority] : 'text-muted')}>{it.anomaly_priority ? t('checklist.prio_' + it.anomaly_priority) : '—'}</td>
                        <td className="px-2 py-2.5 text-muted">{it.photo_required ? '✓' : '—'}</td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="px-1 text-muted hover:text-fg disabled:opacity-30" title={t('checklist.moveUp')}>▲</button>
                          <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="px-1 text-muted hover:text-fg disabled:opacity-30" title={t('checklist.moveDown')}>▼</button>
                          <Button variant="ghost" size="sm" onClick={() => setEditItem(it)}>{t('form.edit')}</Button>
                          <Button variant="ghost" size="sm" onClick={() => delItem(it.id)}>{t('form.delete')}</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* add item row */}
              <div className="flex flex-wrap items-end gap-2 border-t border-divider p-4">
                <input className={cn(sel, 'min-w-[160px] flex-1')} placeholder={t('checklist.itemLabel')} value={ni.label} onChange={(e) => setNi({ ...ni, label: e.target.value })} />
                <select className={sel} value={ni.item_type} onChange={(e) => setNi({ ...ni, item_type: e.target.value })}>
                  {ITEM_TYPES.map((x) => <option key={x} value={x}>{t('checklistType.' + x)}</option>)}
                </select>
                {ni.item_type === 'reading' && (<>
                  <input className={cn(sel, 'w-20')} placeholder={t('checklist.unit')} value={ni.unit} onChange={(e) => setNi({ ...ni, unit: e.target.value })} />
                  <input className={cn(sel, 'w-16')} type="number" placeholder={t('checklist.min')} value={ni.min} onChange={(e) => setNi({ ...ni, min: e.target.value })} />
                  <input className={cn(sel, 'w-16')} type="number" placeholder={t('checklist.max')} value={ni.max} onChange={(e) => setNi({ ...ni, max: e.target.value })} />
                </>)}
                <input className={cn(sel, 'w-24')} placeholder={t('checklist.zone')} value={ni.zone} onChange={(e) => setNi({ ...ni, zone: e.target.value })} />
                <select className={sel} value={ni.anomaly_priority} onChange={(e) => setNi({ ...ni, anomaly_priority: e.target.value })}>
                  <option value="">{t('checklist.priority')}</option>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{t('checklist.prio_' + p)}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-muted"><input type="checkbox" checked={ni.photo_required} onChange={(e) => setNi({ ...ni, photo_required: e.target.checked })} />{t('checklist.photoRequired')}</label>
                <Button size="sm" onClick={addItem}>+ {t('checklist.addItem')}</Button>
              </div>
              {itemErr && <div className="px-4 pb-3 text-xs text-st-overdue-fg">{itemErr}</div>}
            </div>
          )}
        </Card>
      </div>
      <TemplateForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} cats={cats} lines={lines}
        onSaved={(id) => { loadTemplates(); if (id) setSelected(id); }} />
      {editItem && <ItemEditModal item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); if (selected) loadItems(selected); }} />}
    </Layout>
  );
}

const ITEM_LOCALES = ['en', 'fr', 'es', 'ru'] as const;

function ItemEditModal({ item, onClose, onSaved }: { item: Item; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    label: item.label, item_type: item.item_type, unit: item.unit ?? '',
    min: item.min_value?.toString() ?? '', max: item.max_value?.toString() ?? '',
    photo_required: item.photo_required, is_required: item.is_required,
    zone: item.zone ?? '', anomaly_priority: item.anomaly_priority ?? '',
    i18n: { ...(item.label_i18n ?? {}) } as Record<string, string>,
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!f.label.trim()) { setErr(t('form.errRequired')); return; }
    setBusy(true);
    const i18n: Record<string, string> = {};
    for (const l of ITEM_LOCALES) if (f.i18n[l]?.trim()) i18n[l] = f.i18n[l].trim();
    const payload = {
      label: f.label.trim(), label_i18n: i18n,
      item_type: f.item_type,
      unit: f.item_type === 'reading' ? (f.unit.trim() || null) : null,
      min_value: f.item_type === 'reading' && f.min.trim() ? Number(f.min) : null,
      max_value: f.item_type === 'reading' && f.max.trim() ? Number(f.max) : null,
      photo_required: f.photo_required, is_required: f.is_required,
      zone: f.zone.trim() || null, anomaly_priority: f.anomaly_priority || null,
    };
    const res = await supabase.from('checklist_template_items').update(payload as any).eq('id', item.id);
    setBusy(false);
    if (res.error) { setErr(dbErrorMessage(t, res.error)); return; }
    onSaved();
  }

  return (
    <Modal open onClose={onClose} title={t('checklist.editItem')}>
      <form onSubmit={save} className="flex flex-col gap-4 p-5">
        {err && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err}</div>}
        <Field label={t('checklist.itemLabel')} required>
          <TextInput value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          {ITEM_LOCALES.filter((l) => l !== 'en').map((l) => (
            <Field key={l} label={`${t('checklist.itemLabel')} (${l.toUpperCase()})`}>
              <TextInput value={f.i18n[l] ?? ''} onChange={(e) => setF({ ...f, i18n: { ...f.i18n, [l]: e.target.value } })} />
            </Field>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('checklist.itemType')}>
            <Select value={f.item_type} onChange={(e) => setF({ ...f, item_type: e.target.value })}>
              {ITEM_TYPES.map((x) => <option key={x} value={x}>{t('checklistType.' + x)}</option>)}
            </Select>
          </Field>
          <Field label={t('checklist.zone')}>
            <TextInput value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })} />
          </Field>
        </div>
        {f.item_type === 'reading' && (
          <div className="grid grid-cols-3 gap-3">
            <Field label={t('checklist.unit')}><TextInput value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
            <Field label={t('checklist.min')}><TextInput type="number" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })} /></Field>
            <Field label={t('checklist.max')}><TextInput type="number" value={f.max} onChange={(e) => setF({ ...f, max: e.target.value })} /></Field>
          </div>
        )}
        <Field label={t('checklist.priority')}>
          <Select value={f.anomaly_priority} onChange={(e) => setF({ ...f, anomaly_priority: e.target.value })}>
            <option value="">{t('common.none')}</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{t('checklist.prio_' + p)}</option>)}
          </Select>
        </Field>
        <div className="flex gap-4">
          <label className="flex items-center gap-1.5 text-sm text-muted"><input type="checkbox" checked={f.photo_required} onChange={(e) => setF({ ...f, photo_required: e.target.checked })} />{t('checklist.photoRequired')}</label>
          <label className="flex items-center gap-1.5 text-sm text-muted"><input type="checkbox" checked={f.is_required} onChange={(e) => setF({ ...f, is_required: e.target.checked })} />{t('checklist.required')}</label>
        </div>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

function TemplateForm({ open, onClose, onSaved, initial, cats, lines }: {
  open: boolean; onClose: () => void; onSaved: (id?: string) => void; initial: Tpl | null;
  cats: { id: string; name: string }[]; lines: { id: string; name: string }[];
}) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [f, setF] = useState({ name: '', asset_category_id: '', service_line_id: '', status: 'draft' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({ name: initial?.name ?? '', asset_category_id: initial?.asset_category_id ?? '', service_line_id: initial?.service_line_id ?? '', status: initial?.status ?? 'draft' });
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (!f.asset_category_id && !f.service_line_id) er.scope = t('form.errRequired');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = { name: f.name.trim(), asset_category_id: f.asset_category_id || null, service_line_id: f.service_line_id || null, status: f.status };
    const res = initial?.id
      ? await supabase.from('checklist_templates').update(payload).eq('id', initial.id).select('id').single()
      : await supabase.from('checklist_templates').insert({ ...payload, organization_id: orgId } as any).select('id').single();
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved((res.data as any)?.id); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('checklist.edit') : t('checklist.new')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('checklist.name')} required error={err.name}>
          <TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('checklist.category')} error={err.scope}>
            <Select value={f.asset_category_id} error={!!err.scope} onChange={(e) => setF({ ...f, asset_category_id: e.target.value })}>
              <option value="">{t('checklist.any')}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label={t('checklist.serviceLine')} error={err.scope}>
            <Select value={f.service_line_id} error={!!err.scope} onChange={(e) => setF({ ...f, service_line_id: e.target.value })}>
              <option value="">{t('checklist.any')}</option>
              {lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t('checklist.status')}>
          <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{t('checklistStatus.' + s)}</option>)}
          </Select>
        </Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
